import type { GooglePlacesAddressParsed } from "@cpos/shared";
import { Input } from "@/components/ui/base/input";
import { AddressAutocomplete } from "@/components/ui/AddressAutocomplete";
import type { AddressDraft } from "@/application/merchants/business_info_edit";

interface Props {
  value: AddressDraft;
  /** Highlights every input when the address differs from the stored one. */
  changed: boolean;
  onChange: (patch: Partial<AddressDraft>) => void;
}

/** The address inputs; the country is shown but never editable. */
export function AddressFields({ value, changed, onChange }: Props) {
  const highlight = changed ? "border-blue-500" : undefined;

  function applyPlace(place: GooglePlacesAddressParsed) {
    onChange({
      line1: place.line1 ?? "",
      line2: place.line2 ?? "",
      city: place.city ?? "",
      state: place.state ?? "",
      postalCode: place.postalCode ?? "",
    });
  }

  return (
    <>
      <div className={changed ? "[&_input]:border-blue-500" : undefined}>
        <AddressAutocomplete
          value={value.line1}
          onChange={(line1) => onChange({ line1 })}
          onAddressSelected={applyPlace}
          regions={[value.country.toLowerCase()]}
        />
      </div>
      <Input
        aria-label="Suite or unit"
        placeholder="Suite or unit (optional)"
        className={highlight}
        value={value.line2}
        onChange={(e) => onChange({ line2: e.target.value })}
      />
      <div className="grid grid-cols-3 gap-2">
        <Input
          aria-label="City"
          placeholder="City"
          className={highlight}
          value={value.city}
          onChange={(e) => onChange({ city: e.target.value })}
        />
        <Input
          aria-label="Province or state"
          placeholder="Province"
          className={highlight}
          value={value.state}
          onChange={(e) => onChange({ state: e.target.value })}
        />
        <Input
          aria-label="Postal code"
          placeholder="Postal code"
          className={highlight}
          value={value.postalCode}
          onChange={(e) => onChange({ postalCode: e.target.value })}
        />
      </div>
      <p className="text-muted-foreground text-xs">
        Country: {value.country}. It can't be changed here.
      </p>
    </>
  );
}
